// R1c integrations — Connect / Rotate credential drawer.
// Owner-only interactions. Values are POSTed to /api/integrations/{key}/connect
// which stores them encrypted server-side. The drawer never displays or
// echoes back full plaintext values after save — the parent Integrations
// page re-fetches and shows only masked previews.
import React, { useState, useEffect } from "react";
import { X, ExternalLink, ShieldCheck, KeyRound } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

export default function IntegrationConnectDrawer({
  open, adapter, onClose, onSaved, mode = "connect",
}) {
  const [values, setValues] = useState({});
  const [useEmergent, setUseEmergent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !adapter) return;
    // Prefill sensible defaults from catalog field.default.
    const seed = {};
    (adapter.fields || []).forEach((f) => { if (f.default) seed[f.key] = f.default; });
    setValues(seed);
    setUseEmergent(false);
  }, [open, adapter]);

  if (!open || !adapter) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = mode === "rotate"
        ? `/integrations/${adapter.key}/rotate`
        : `/integrations/${adapter.key}/connect`;
      const r = await api.post(endpoint, {
        values, use_emergent_llm_key: useEmergent,
      });
      if (r.data.ok) {
        toast.success(`${adapter.label || adapter.key} connected · ${r.data.state}`);
      } else {
        toast.warning(`${adapter.label || adapter.key} · ${r.data.state} · ${r.data.detail}`);
      }
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const oauthLine = adapter.oauth_client === "awaiting_owner_provider_account"
    ? "OAuth client credentials must be supplied by you — Emergent has not registered a shared app for this provider."
    : adapter.oauth_client === "required_emergent_registration"
      ? "This adapter requires Emergent to register an OAuth client. Awaiting platform action."
      : adapter.oauth_client === "registered"
        ? "OAuth client registered — sign in with the button below."
        : null;

  return (
    <div className="fixed inset-0 z-[70] flex" data-testid={`integration-drawer-${adapter.key}`}>
      <button
        className="flex-1 bg-black/40"
        aria-label="Close drawer"
        onClick={onClose}
        data-testid={`integration-drawer-scrim-${adapter.key}`}
      />
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl border-l border-erf-borderCream flex flex-col">
        <div className="flex items-center justify-between border-b border-erf-borderCream px-4 py-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">
              {mode === "rotate" ? "Rotate credentials" : "Connect adapter"} · Tier {adapter.tier || "?"}
            </div>
            <div className="font-display font-semibold text-erf-dark text-lg">
              {adapter.label || adapter.key}
            </div>
          </div>
          <button
            type="button"
            className="p-1 rounded hover:bg-erf-borderCream/40"
            onClick={onClose}
            aria-label="Close"
            data-testid={`integration-drawer-close-${adapter.key}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 flex flex-col">
          <div className="p-4 space-y-4 flex-1">
            <p className="text-xs font-nav text-erf-dark/70">{adapter.description}</p>
            {oauthLine && (
              <div className="text-xs font-nav border border-amber-200 bg-amber-50 text-amber-900 rounded px-3 py-2 flex gap-2" data-testid={`integration-oauth-notice-${adapter.key}`}>
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                <span>{oauthLine}</span>
              </div>
            )}

            {adapter.supports_emergent_key && (
              <label className="flex items-start gap-2 border border-erf-borderCream rounded p-3 cursor-pointer" data-testid={`integration-emergent-toggle-${adapter.key}`}>
                <input
                  type="checkbox"
                  checked={useEmergent}
                  onChange={(e) => setUseEmergent(e.target.checked)}
                  className="mt-0.5"
                  data-testid={`integration-emergent-toggle-input-${adapter.key}`}
                />
                <div>
                  <div className="text-sm font-nav font-semibold text-erf-dark flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5" />
                    Use Emergent Universal Key
                  </div>
                  <div className="text-[11px] font-nav text-erf-dark/70 mt-0.5">
                    Route through the Emergent LLM proxy instead of pasting your own {adapter.label} key. Billed to your Emergent plan.
                  </div>
                </div>
              </label>
            )}

            {!useEmergent && (adapter.fields || []).map((f) => {
              // Stage A · Twilio credential-persistence repair — fields already
              // present in the encrypted vault render as "Stored securely" so
              // the Owner never has to re-enter (or accidentally blank-out)
              // retained values. Only genuinely missing fields prompt for input.
              const stored = adapter.masked_preview && adapter.masked_preview[f.key];
              const hasStored = Boolean(stored) && mode !== "rotate";
              return (
              <div key={f.key} className="space-y-1" data-testid={`integration-fieldrow-${adapter.key}-${f.key}`}>
                <label
                  htmlFor={`field-${adapter.key}-${f.key}`}
                  className="text-[11px] font-mono uppercase tracking-widest text-erf-dark/70"
                >
                  {f.label}
                  {f.sensitive && <span className="ml-1 text-erf-gold">· sensitive</span>}
                  {hasStored && <span className="ml-2 text-emerald-700 font-nav normal-case tracking-normal">· Stored securely</span>}
                </label>
                {hasStored ? (
                  <div
                    data-testid={`integration-field-stored-${adapter.key}-${f.key}`}
                    className="w-full px-3 py-2 border border-emerald-200 bg-emerald-50/40 rounded text-sm font-mono text-erf-dark/70 flex items-center justify-between"
                  >
                    <span>{stored}</span>
                    <button
                      type="button"
                      className="text-[10px] font-nav text-erf-gold underline"
                      onClick={() => setValues({ ...values, [f.key]: "" })}
                      data-testid={`integration-field-clear-${adapter.key}-${f.key}`}
                    >
                      Change
                    </button>
                  </div>
                ) : f.multiline ? (
                  <textarea
                    id={`field-${adapter.key}-${f.key}`}
                    rows={5}
                    placeholder={f.placeholder || ""}
                    value={values[f.key] || ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    className="w-full px-3 py-2 border border-erf-borderCream rounded text-sm font-mono focus:outline-none focus:border-erf-gold"
                    data-testid={`integration-field-${adapter.key}-${f.key}`}
                    autoComplete="off"
                    spellCheck={false}
                  />
                ) : (
                  <input
                    id={`field-${adapter.key}-${f.key}`}
                    type={f.sensitive || f.key.toLowerCase().includes("secret") || f.key.toLowerCase().includes("token") || f.key.toLowerCase().includes("key") ? "password" : "text"}
                    placeholder={f.placeholder || ""}
                    value={values[f.key] || ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    className="w-full px-3 py-2 border border-erf-borderCream rounded text-sm font-mono focus:outline-none focus:border-erf-gold"
                    data-testid={`integration-field-${adapter.key}-${f.key}`}
                    autoComplete="off"
                    spellCheck={false}
                  />
                )}
                {f.help && !hasStored && (
                  <div className="text-[10px] font-nav text-erf-dark/60">{f.help}</div>
                )}
              </div>
              );
            })}

            {adapter.help_url && (
              <a
                href={adapter.help_url}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-mono text-erf-gold hover:underline inline-flex items-center gap-1"
                data-testid={`integration-help-link-${adapter.key}`}
              >
                Open provider console <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          <div className="border-t border-erf-borderCream p-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy}
              data-testid={`integration-drawer-cancel-${adapter.key}`}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-erf-dark text-erf-cream hover:bg-erf-dark/90"
              data-testid={`integration-drawer-submit-${adapter.key}`}
            >
              {busy
                ? "Verifying with provider…"
                : mode === "rotate" ? "Rotate & verify" : "Connect & verify"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
